'use client';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ArrowDown from 'public/images/arrow-down.svg';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger
} from 'components/ui/accordion';
import { sortArrayAlphabetically } from 'shared/utils';
import { Button } from 'components/ui/button';
import Image from 'next/image';

export default function ProviderDataCenters({ provider }) {
  const { t } = useTranslation();

  const [showAllDC, setShowAllDC] = useState(false);
  const ref = useRef<HTMLHeadingElement>(null);

  const { dataCenters: dc } = provider;

  const dataCenters: {
    isSovereignCloud: boolean | undefined;
    country: string;
    city: string;
    sovereignCloudApplication?: {
      contentStatus: string;
    };
  }[] = dc
    ?.flatMap((dataCenter) => {
      return dataCenter.locations?.map((location) => {
        return {
          country: dataCenter?.country,
          city:
            dataCenter?.country === 'United States'
              ? `${location?.city}, ${location?.state}`
              : location?.state,
          isSovereignCloud:
            dataCenter?.sovereignCloudApplication?.contentStatus === 'APPROVED'
        };
      });
    })
    ?.filter((e) => !!e);

  const dataCentersByCountry =
    dataCenters?.reduce<
      {
        country: string;
        cities: string[];
        isSovereignCloud?: boolean;
      }[]
    >((result, currentDc) => {
      if (result.some((e) => e.country === currentDc.country)) {
        const index = result.findIndex((e) => e.country === currentDc.country);
        let prevResult = [...result];
        prevResult[index] = {
          ...prevResult[index],
          cities: Array.from(
            new Set([...prevResult[index].cities, currentDc.city])
          ),
          isSovereignCloud: prevResult[index].isSovereignCloud
            ? prevResult[index].isSovereignCloud
            : currentDc?.isSovereignCloud
        };
        return prevResult;
      } else {
        return result.concat([
          {
            country: currentDc.country,
            cities: [currentDc.city],
            isSovereignCloud: currentDc?.isSovereignCloud
          }
        ]);
      }
    }, []) || [];

  return dataCentersByCountry?.length > 0 ? (
    <div className="bg-white rounded-md py-12 px-6 flex flex-col gap-8">
      <div className="flex flex-col items-start gap-4">
        <h3 ref={ref} className="p-0 m-0 text-lg font-normal text-black">
          {t('data_centers_location')}
        </h3>
        <Accordion collapsible type="single" className="w-full">
          {sortArrayAlphabetically(dataCentersByCountry, 'country')
            .filter((_, idx) => idx < (showAllDC ? dataCenters.length : 12))
            .map((dc, idx) => (
              <AccordionItem
                key={`dc_${idx}`}
                value={dc.country}
                className="my-2"
              >
                <AccordionTrigger>
                  <div className="flex items-center gap-1">
                    {dc.country}
                    <span className="!text-[#65696D] !font-light">
                      ({dc.cities.length})
                    </span>
                    {dc.isSovereignCloud && (
                      <Image
                        src={'/images/sovereign-cloud-icon.svg'} //mudar icone
                        width={35}
                        height={35}
                        quality={100}
                        alt="cloud icon"
                        className="self-center ml-2"
                      />
                    )}
                  </div>
                </AccordionTrigger>
                <AccordionContent>
                  <ul className="px-4 pt-2 pb-4 m-0 text-sm font-light text-[#53565a]">
                    {dc.cities.map((city, idx) => (
                      <li
                        key={`li_item${idx}`}
                        className="list-inside py-[1px]"
                      >
                        {city}
                      </li>
                    ))}
                  </ul>
                </AccordionContent>
              </AccordionItem>
            ))}
        </Accordion>
        {dataCentersByCountry?.length > 12 && (
          <Button
            variant="unstyled"
            size="unstyled"
            className="text-[#2360A0] text-[15px] font-normal cursor-pointer"
            onClick={() => {
              setShowAllDC(!showAllDC);
              if (showAllDC) {
                if (ref.current)
                  ref.current.scrollIntoView({
                    behavior: 'smooth'
                  });
              }
            }}
          >
            {showAllDC ? t('view_less') : t('view_more')}
            <ArrowDown className={showAllDC ? 'rotate-180' : ''} />
          </Button>
        )}
      </div>
    </div>
  ) : null;
}
